import * as React from 'react'

export function useFullscreen(containerRef: React.RefObject<HTMLDivElement | null>) {
  const [isFullscreen, setIsFullscreen] = React.useState(false)

  const toggleFullscreen = React.useCallback(async () => {
    const el = containerRef.current
    if (!el) return

    if (isFullscreen) {
      const doc = document as Document & {
        webkitFullscreenElement?: Element
        webkitExitFullscreen?: () => Promise<void>
      }
      if (document.fullscreenElement || doc.webkitFullscreenElement) {
        try {
          if (document.exitFullscreen) {
            await document.exitFullscreen()
          } else if (doc.webkitExitFullscreen) {
            await doc.webkitExitFullscreen()
          }
        } catch {
          /**/
        }
      }
      el.classList.remove('pseudo-fullscreen')
      setIsFullscreen(false)
      return
    }

    try {
      const elAny = el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> }
      if (el.requestFullscreen) {
        await el.requestFullscreen()
      } else if (elAny.webkitRequestFullscreen) {
        await elAny.webkitRequestFullscreen()
      } else {
        throw new Error('no Fullscreen API')
      }
    } catch {
      el.classList.add('pseudo-fullscreen')
      setIsFullscreen(true)
    }
  }, [containerRef, isFullscreen])

  React.useEffect(() => {
    const onFsChange = () => {
      const doc = document as Document & { webkitFullscreenElement?: Element }
      const nativeFs = !!document.fullscreenElement || !!doc.webkitFullscreenElement
      setIsFullscreen(nativeFs)
      if (!nativeFs && containerRef.current) {
        containerRef.current.classList.remove('pseudo-fullscreen')
      }
    }
    document.addEventListener('fullscreenchange', onFsChange)
    document.addEventListener('webkitfullscreenchange', onFsChange)
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange)
      document.removeEventListener('webkitfullscreenchange', onFsChange)
    }
  }, [containerRef])

  return { isFullscreen, toggleFullscreen }
}
